/**
 * @file TetherAppBar.js
 * @summary A completely custom AppBar for a react-admin v3 application.
 * @description This file creates and exports a single component, `TetherAppBar`, which
 * replaces the default react-admin AppBar. It includes a responsive user menu
 * with a profile picture, the user's full name, and a popover logout button.
 * It is built to be compatible with react-admin v3 and Material-UI v4.
 *
 * It contains two internal components:
 * 1. TetherUserMenu: The logic and view for the user profile area on the right.
 * 2. TetherAppBar: The main AppBar container that orchestrates the layout.
 */

import * as React from "react";
import { useState } from "react";

// For react-admin v3, these are the correct imports for core components and hooks.
import { AppBar, useGetIdentity, Logout } from "react-admin";

// For react-admin v3, UI components and utilities come from '@material-ui/core'.
import {
    Typography,
    Avatar,
    Menu,
    MenuItem,
    withWidth, // This is a Higher-Order Component (HOC) to detect screen size.
} from "@material-ui/core";

// -------------------------------------------------------------------------
// Component #1: The Custom User Menu
// This component handles the entire right side of the AppBar, including the
// popover menu and responsive logic for hiding the user's name on mobile.
// -------------------------------------------------------------------------

const TetherUserMenu = props => {
    /**
     * The `width` prop ('xs', 'sm', etc.) is automatically provided by the
     * `withWidth()` HOC that wraps this component at the bottom of the file.
     */
    const { width } = props;
    const { identity, isLoading } = useGetIdentity();

    // This state manages the popover menu for the logout button.
    // `anchorEl` stores the element that the menu should attach to.
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    // To prevent errors, we render nothing until the user's identity has been fetched.
    if (isLoading || !identity) {
        return null;
    }

    // Basic inline styles for layout.
    const styles = {
        root: {
            display: "flex",
            alignItems: "center",
            cursor: "pointer", // Indicates the entire area is clickable.
            // marginRight: "1em",
        },
        avatar: {
            width: 32,
            height: 32,
        },
        welcome: {
            marginLeft: "1em",
            color: "white",
        },
    };

    return (
        <div>
            {/* This is the visible part of the menu that the user clicks. */}
            <div style={styles.root} onClick={handleClick}>
                <Avatar
                    style={styles.avatar}
                    src={identity.avatar} // Assumes your authProvider returns an `avatar` URL.
                    alt={identity.fullName}
                >
                    {/* Fallback if no avatar: show the first initial of the user's name. */}
                    {identity.fullName ? identity.fullName.charAt(0) : ""}
                </Avatar>

                {/*
                  REQUIREMENT #4: Conditionally render the name.
                  If the screen width is extra-small ('xs'), the name is not rendered.
                */}
                {width !== "xs" && (
                    <Typography style={styles.welcome} variant="body1">
                        {identity.fullName}
                    </Typography>
                )}
            </div>

            {/*
              REQUIREMENT #3: The popover menu that appears on click.
              It is anchored to the element stored in `anchorEl`.
            */}
            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                // These props ensure the menu appears correctly UNDER the profile.
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
            >
                {/* The Logout button must be wrapped in a MenuItem for styling and to handle the close action. */}
                {/* <MenuItem> */}
                {/* onClick={handleClose} */}
                <Logout />
                {/* </MenuItem> */}
            </Menu>
        </div>
    );
};

/**
 * We export the wrapped version of the component. The `withWidth()` HOC
 * is the standard way to inject screen size information in this version of Material-UI.
 */
const TetherUserMenuWithWidth = withWidth()(TetherUserMenu);

// -------------------------------------------------------------------------
// Component #2: The Final, Exported AppBar
// This component assembles the final AppBar layout.
// -------------------------------------------------------------------------

export const TetherAppBar = props => {
    const styles = {
        title: {
            flex: 1, // This makes the title take up all available space, pushing other items right.
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
        },
    };

    return (
        /**
         * The `userMenu` prop is the official way to REPLACE the default user menu.
         * We pass our complete, responsive, and interactive menu component to it.
         */
        <AppBar {...props} userMenu={<TetherUserMenuWithWidth />}>
            {/**
             * REQUIREMENT #2: The guaranteed way to display the page title.
             * React-admin's core is hardcoded to find the element with this specific ID
             * and will inject the current page's title text into it.
             */}
            <Typography variant="subtitle2" color="inherit" style={styles.title} id="react-admin-title" />
        </AppBar>
    );
};
